'use client';
import React from 'react';
import { MemberProfile } from '@/lib/supabase/types';
import { PreviewProfile } from '@/lib/supabase/types';
import { Heart, X, Eye, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { getPublicDisplayName } from '@/lib/supabase/nameUtils';

interface ProfileCardProps {
  profile: MemberProfile | (PreviewProfile & { profileStatus?: string; profileComplete?: number; membershipTier?: string });
  isLiked: boolean;
  onLike: () => void;
  onView: () => void;
  onPass: () => void;
  isPhotoBlocked?: boolean;
  photoStatus?: string;
  isPreview?: boolean;
}

export default function ProfileCard({ profile, isLiked, onLike, onView, onPass, isPhotoBlocked, photoStatus, isPreview }: ProfileCardProps) {
  const publicName = getPublicDisplayName({
    firstName: (profile as MemberProfile).firstName,
    lastName: (profile as MemberProfile).lastName,
    displayName: profile.displayName,
  });

  const handleLike = () => {
    onLike();
    if (!isPhotoBlocked && !isLiked) {
      if (isPreview) {
        toast.success(`You liked ${publicName}!`, {
          description: 'This is a preview profile. Keep browsing for active members.',
        });
      } else {
        toast.success(`You liked ${publicName}!`, {
          description: "If they like you back, it's a match.",
        });
      }
    }
  };

  const handlePass = () => {
    onPass();
    toast(`Passed on ${publicName}`, { description: 'Profile removed from your Discover view.' });
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden card-hover group transition-all duration-300">
      {/* Photo */}
      <div
        className="relative h-52 flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${profile.avatarColor}18, ${profile.avatarColor}35)` }}
      >
        {profile.photoUrl ? (
          <img
            src={profile.photoUrl}
            alt={`Profile photo of ${publicName}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : (
          <div
            className="w-24 h-24 rounded-2xl flex items-center justify-center text-white font-black text-3xl shadow-lg"
            style={{ background: `linear-gradient(135deg, ${profile.avatarColor}dd, ${profile.avatarColor}88)` }}
          >
            {profile.avatarInitials}
          </div>
        )}
      </div>

      {/* Fast-preview info */}
      <div className="p-4 space-y-3">
        {/* Name / Age + Location */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-bold text-foreground text-base leading-tight">
              {publicName}, {profile.age}
            </h3>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <MapPin size={11} />{profile.usState}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs font-medium text-foreground">{profile.kenyanCounty}</div>
            <div className="text-xs text-muted-foreground">Kenyan County</div>
          </div>
        </div>

        {/* Intention chip */}
        <div>
          <span
            className="text-xs px-2.5 py-1 rounded-full font-medium border"
            style={{ backgroundColor: '#008000', color: '#ffffff', borderColor: '#008000' }}
          >
            {profile.intention}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={handleLike}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              isPhotoBlocked
                ? 'bg-muted/20 border border-dashed border-[#FFBF00]/40 text-[#FFBF00]/70 cursor-not-allowed'
                : ''
            }`}
            style={
              !isPhotoBlocked
                ? isLiked
                  ? { backgroundColor: 'rgba(0,128,0,0.2)', border: '1px solid #008000', color: '#008000' }
                  : {}
                : {}
            }
            aria-label={isLiked ? 'Unlike profile' : 'Like profile'}
          >
            {!isPhotoBlocked && !isLiked ? (
              <span className="flex items-center gap-1.5 text-muted-foreground hover:text-[#008000] transition-colors w-full justify-center">
                <Heart size={15} />Like
              </span>
            ) : (
              <>
                <Heart size={15} className={isLiked && !isPhotoBlocked ? 'fill-[#008000]' : ''} />
                {isLiked && !isPhotoBlocked ? 'Liked' : 'Like'}
              </>
            )}
          </button>

          <button
            onClick={handlePass}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold bg-muted/20 border border-border text-muted-foreground hover:border-border hover:text-foreground transition-all"
            aria-label="Pass on profile"
          >
            <X size={15} />Pass
          </button>

          <button
            onClick={onView}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200"
            style={{ backgroundColor: '#750000' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#5a0000')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#750000')}
            aria-label="View full profile"
          >
            <Eye size={15} />View
          </button>
        </div>
      </div>
    </div>
  );
}